import Droppable from 'owa-dnd/lib/components/Droppable';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { type FolderForestNodeType } from 'owa-favorites-types';
import type { FolderForestTreeType } from 'owa-graph-schema';
import { showFolderTreeContextMenu } from 'owa-mail-folder-store/lib/actions/folderTreeContextMenu';
import type MailFolderDragData from 'owa-mail-folder-store/lib/store/schema/MailFolderDragData';
import { getAnchorForContextMenu } from 'owa-positioning';
import type { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import React from 'react';
import type { MailboxInfo } from 'owa-client-types';
import type { MailFolderRootFragment } from 'owa-mail-folder-view-graphql';
import { SourceMap } from 'owa-folders-constants';
import type { FluentIcon } from '@fluentui/react-icons';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

interface MailFolderRootProps {
    rootFolder: MailFolderRootFragment; // Root folder details
    shouldBeDroppable: boolean;
    displayName: string;
    rootNodeId: string;
    treeType: FolderForestTreeType; // Type of folder tree
    mailboxInfo: MailboxInfo;
    style?: React.CSSProperties;
    moveFolder: (
        destinationFolderId: string,
        destinationFolderMailboxInfo: MailboxInfo,
        sourceFolderId: string,
        sourceFolderMailboxInfo: MailboxInfo,
        sourceFolderParentFolderId: string,
        sourceFolderDisplayName: string
    ) => void;
    customIconComponent?: FluentIcon;
    hideIcon?: boolean;
    customDepth?: number;

    shouldShowRootNodeContextMenu: boolean;
    isRootExpanded: boolean;
    onRootNodeChevronClickedCallback: () => void; // Callback on click of root folder
    ellipsesOnHover?: boolean;
    id?: string;
    elementRef?: React.RefObject<HTMLDivElement | null>;
    renderCustomTreeNodeDisplay?: (textClassName: string) => React.JSX.Element;
}

export default function MailFolderRoot(props: MailFolderRootProps): React.JSX.Element {
    const { isRootExpanded } = props;
    const onRootNodeChevronClicked = React.useCallback(
        (evt: React.MouseEvent<unknown> | KeyboardEvent) => {
            evt.stopPropagation();
            props.onRootNodeChevronClickedCallback();
        },
        [props.onRootNodeChevronClickedCallback]
    );

    const chevronProps: ChevronProps = React.useMemo(() => {
        return {
            isExpanded: isRootExpanded,
            onClick: onRootNodeChevronClicked,
            isAnimated: true,
        };
    }, [isRootExpanded, onRootNodeChevronClicked]);

    const emptyDropViewState = createDropViewState();
    const onDrop = React.useCallback(
        (dragData: DragData) => {
            const mailFolderItemBeingDragged = dragData as MailFolderDragData;
            const draggedFolderId = mailFolderItemBeingDragged.folderId;
            const draggedFolderMailboxInfo = mailFolderItemBeingDragged.mailboxInfo;
            const draggedFolderParentFolderId = mailFolderItemBeingDragged.parentFolderId;
            props.moveFolder(
                props.rootFolder.id /*destinationFolderId */,
                props.mailboxInfo /* destinationFolderMailboxInfo */,
                draggedFolderId /* sourceFolderId */,
                draggedFolderMailboxInfo /* sourceFolderMailboxInfo */,
                draggedFolderParentFolderId /* parentFolderId */,
                mailFolderItemBeingDragged.displayName
            );
        },
        [props.rootFolder?.id, props.mailboxInfo.type]
    );

    const canDrop = React.useCallback(
        (dragData: DragData) => {
            const itemType = dragData.itemType;
            return (
                itemType === DraggableItemTypes.MailFolderNode &&
                props.shouldBeDroppable &&
                !!props.rootFolder
            );
        },
        [props.shouldBeDroppable, !!props.rootFolder]
    );

    const onRootNodeContextMenu = React.useCallback(
        (evt: React.MouseEvent<HTMLElement>) => {
            evt.preventDefault();
            if (props.shouldShowRootNodeContextMenu) {
                showFolderTreeContextMenu(
                    props.rootFolder.id,
                    0,
                    props.rootFolder.id,
                    getAnchorForContextMenu(evt),
                    props.treeType,
                    props.rootNodeId,
                    true /* showRootNodeMenu */
                );
            }
        },
        [
            props.shouldShowRootNodeContextMenu,
            props.rootFolder?.id,
            props.rootFolder,
            props.treeType,
            props.rootNodeId,
        ]
    );

    return (
        /* Render root node */
        <Droppable
            dropViewState={emptyDropViewState}
            onDrop={onDrop}
            canDrop={canDrop}
            style={props.style}
        >
            <TreeNode
                elementRef={props.elementRef}
                chevronProps={chevronProps}
                depth={props.customDepth !== undefined ? props.customDepth : 0} // Started at 0 depth
                displayName={props.displayName}
                isRootNode={!props.customIconComponent} // if custom icon is defined, then it is not a root node
                onClick={chevronProps.onClick}
                onContextMenu={onRootNodeContextMenu}
                customIconComponent={props.customIconComponent}
                hideIcon={props.hideIcon}
                ellipsesOnHover={props.ellipsesOnHover}
                source={SourceMap.account}
                shouldShowFocusBorder={true}
                id={props.id}
                renderCustomTreeNodeDisplay={props.renderCustomTreeNodeDisplay}
                mailboxInfo={props.mailboxInfo}
                useNewSelectionUI={isGlobalFeatureEnabled('fp-newfoldersselection-ui')}
            />
        </Droppable>
    );
}
